import { GetInferredFromRaw, getPaginatedZod } from '@thinknimble/tn-models'
import { z } from 'zod'

export const surveyQuestionOptionShape = {
  id: z.string().uuid().readonly(),
  question: z.string().uuid(),
  display: z.string(),
  value: z
    .string()
    .max(255)
    .nullable()
    .describe(
      'The value to use when exporting the data to fit the current workflow, this will be autofilled',
    )
    .optional(),
  cleanDisplay: z.string().readonly(),
  order: z.number().int().gte(-2147483648).lte(2147483647).optional(),
  translations: z.record(z.string(), z.string()),
}

export const surveyResponseValueShape = {
  id: z.string().uuid().readonly(),
  question: z.string().uuid(),
  value: z.string().nullable().optional(),
  instance: z.string().uuid(),
  valueRef: z.object(surveyQuestionOptionShape).optional().nullable(),
}

export const paginatedQuestionOpts = getPaginatedZod(surveyQuestionOptionShape)
export const questionShape = {
  id: z.string().uuid().readonly(),
  question: z.string().nullable().optional(),
  questionType: z
    .enum(['MULTIPLE_OPTION', 'SINGLE_OPTION', 'TEXT', 'NONE'])
    .describe(
      '* `MULTIPLE_OPTION` - Multiple Option\n* `SINGLE_OPTION` - Single Option\n* `TEXT` - Text\n* `NONE` - None',
    )
    .optional(),
  questionDisplay: z
    .enum([
      'GRID_LAYOUT',
      'DROPDOWN',
      'LIST_HORIZONTAL',
      'LIST_VERTICAL',
      'OPEN_TEXT',
      'DATE',
      'NONE',
    ])
    .describe(
      '* `GRID_LAYOUT` - Grid Layout\n* `DROPDOWN` - Dropdown\n* `LIST_HORIZONTAL` - Horizontal List\n* `LIST_VERTICAL` - Vertical List\n* `OPEN_TEXT` - Open Text\n* `DATE` - Date\n* `NONE` - None',
    )
    .optional(),
  componentType: z
    .enum([
      'DROPDOWN',
      'CHECKBOX',
      'RADIO_BUTTON',
      'TEXT_INPUT',
      'TEXT_AREA',
      'NUMBER_INPUT',
      'DATE_PICKER',
      'FILE_UPLOAD',
      'ADDRESS',
      'PHONE_INPUT',
      'NONE',
    ])
    .describe('The UI widget type used to render this question')
    .optional(),
  layout: z.enum(['VERTICAL', 'HORIZONTAL', 'GRID', 'NONE']).optional(),
  nextQuestion: z.string().uuid().nullable().optional(),
  options: z.array(z.string().uuid()),
  optionsRef: z.array(z.object(surveyQuestionOptionShape)).readonly(),
  validators: z.array(z.string().uuid()),
  validatorsRef: z
    .array(
      z.object({
        id: z.string().uuid().readonly(),
        question: z.string().uuid(),
        validationType: z
          .enum(['REQUIRED', 'MIN_CHOICE_LEN', 'MAX_CHOICE_LEN'])
          .describe(
            '* `REQUIRED` - Required\n* `MIN_CHOICE_LEN` - Minimum (Inclusive)\n* `MAX_CHOICE_LEN` - Maximum Choice Length (Inclusive)',
          ),
        choiceLenValue: z.number().int().gte(-2147483648).lte(2147483647).nullable().optional(),
      }),
    )
    .readonly(),
  isFirst: z.boolean().optional(),
  isLast: z.boolean().optional(),
  response: z.string().readonly(),
  responseRef: z.object(surveyResponseValueShape).readonly(),
  translations: z.record(z.string(), z.string()),
}

export const surveyTemplateShape = {
  firstQuestion: z.string().uuid().nullable().readonly(),
  lastQuestion: z.string().uuid().nullable().readonly(),
  primaryLanguage: z.string().nullable().readonly(),
  otherLanguages: z.array(z.string()),
  questions: z.array(z.string().uuid()),
  questionsRef: z.array(z.object(questionShape)),
}

export const surveyInstanceShape = {
  id: z.string().uuid().readonly(),
  template: z.string().uuid(),
  templateRef: z.object(surveyTemplateShape),
  cursor: z.string().uuid().readonly(),
  cursorRef: z
    .object({
      id: z.string().uuid().readonly(),
      instance: z.string().uuid(),
      currentQuestion: z.string().uuid().nullable().readonly(),
      currentQuestionRef: z.object(questionShape).readonly(),
    })
    .readonly(),
  requiresPinSetup: z.boolean().optional(),
  isPinProtected: z.boolean().optional(),
}

const structureStyleShape = {
  score: z.number(),
  label: z.string(),
}

const energyStyleShape = {
  score: z.number(),
  label: z.string(),
}

const motivatorsShape = {
  Creativity: z.number(),
  Expertise: z.number(),
  Income: z.number(),
  Meaning: z.number(),
  Relationships: z.number(),
  Status: z.number(),
}

export const surveyInstanceReportShape = {
  motivators: z.object(motivatorsShape).readonly(),
  energy_style: z.object(energyStyleShape).readonly(),
  structure_style: z.object(structureStyleShape).readonly(),
}

export const surveyCursorShape = {
  id: z.string().uuid().readonly(),
  instance: z.string().uuid(),
  currentQuestion: z.string().uuid().nullable().readonly(),
  currentQuestionRef: z.string().readonly(),
}

export const surveyInstanceCreateShape = {
  template: z.string().uuid(),
}

export const surveyResponseShape = {
  errors: z.array(z.string()),
  response: z.string().uuid().optional(),
  responseRef: z.object(surveyResponseValueShape).readonly(),
  instance: z.string().uuid().optional(),
  instanceRef: z
    .object({
      id: z.string().uuid().readonly(),
      template: z.string().uuid(),
      templateRef: z.object(surveyTemplateShape).readonly(),
      cursor: z.string().uuid().readonly(),
      cursorRef: z
        .object({
          id: z.string().uuid().readonly(),
          instance: z.string().uuid(),
          currentQuestion: z.string().uuid().nullable().readonly(),
          currentQuestionRef: z.object(questionShape).readonly(),
        })
        .readonly(),
    })
    .readonly(),
}

export const surveyResponseCreateShape = {
  question: z.string().uuid().optional(),
  value: z.any().nullable().optional(),
  instance: z.string().uuid().optional(),
}
export const surveyResponseUpdateShape = {
  ...surveyResponseCreateShape,
  id: z.string().uuid(),
}

export const choiceOptionFilterShape = {
  question: z.string(),
  search: z.string(),
  // pagination doesnt seem to work for standalone
  page: z.number(),
  size: z.number(),
}

export type SurveyResponseCreate = GetInferredFromRaw<typeof surveyResponseCreateShape>
export type SurveyResponseUpdate = GetInferredFromRaw<typeof surveyResponseUpdateShape>
export type SurveyCursor = GetInferredFromRaw<typeof surveyCursorShape>

export type SurveyInstance = GetInferredFromRaw<typeof surveyInstanceShape>
export type SurveyInstanceCreate = GetInferredFromRaw<typeof surveyInstanceCreateShape>
export type SurveyResponse = GetInferredFromRaw<typeof surveyResponseShape>
export type SurveyQuestionShape = GetInferredFromRaw<typeof questionShape>

export type SurveyQuestionOption = GetInferredFromRaw<typeof surveyQuestionOptionShape>
export type SurveyTemplateShape = GetInferredFromRaw<typeof surveyTemplateShape>
export type ChoiceOptionFilter = GetInferredFromRaw<typeof choiceOptionFilterShape>

export type MotivatorsShape = GetInferredFromRaw<typeof motivatorsShape>
export type EnergyStyleShape = GetInferredFromRaw<typeof energyStyleShape>
export type StructureStyleShape = GetInferredFromRaw<typeof structureStyleShape>

export type SurveyChoiceOption = GetInferredFromRaw<typeof surveyQuestionOptionShape>

// ============================================================================
// PATTERN PRESETS
// ============================================================================

/**
 * Pattern presets for PATTERN validator
 */
export const PATTERN_PRESETS = [
  { key: 'email', label: 'Email', pattern: String.raw`^[\w.+-]+@[\w-]+\.[\w.]+$` },
  { key: 'phone', label: 'Phone', pattern: String.raw`^\+?[\d\s\-().]+$` },
  { key: 'url', label: 'URL', pattern: String.raw`^https?://\S+$` },
  { key: 'alpha', label: 'Letters Only', pattern: String.raw`^[a-zA-Z\s]+$` },
  { key: 'alphanumeric', label: 'Alphanumeric', pattern: String.raw`^[a-zA-Z0-9\s]+$` },
] as const

export type PatternPresetKey = (typeof PATTERN_PRESETS)[number]['key']

// ============================================================================
// ADDRESS VALUE
// ============================================================================

/**
 * Zod shape for ADDRESS component type responses.
 * Mirrors the backend AddressValue Pydantic model.
 */
export const addressValueShape = {
  rawAddress: z.string(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  placeId: z.string().nullable().optional(),
}

export type AddressValue = GetInferredFromRaw<typeof addressValueShape>

// ============================================================================
// FILE UPLOAD PRESIGNED URL SHAPES
// ============================================================================

export const presignedUploadInputShape = {
  filename: z.string(),
  contentType: z.string(),
  questionId: z.string().uuid(),
  instance: z.string().uuid(),
}

export const presignedUploadOutputShape = {
  uploadUrl: z.string().url(),
  objectKey: z.string(),
}

export const presignedDownloadInputShape = {
  objectKey: z.string(),
  instance: z.string().uuid(),
  filename: z.string().optional(),
}

export const presignedDownloadOutputShape = {
  downloadUrl: z.string().url(),
}

export const confirmUploadInputShape = {
  objectKey: z.string(),
  questionId: z.string().uuid(),
  instance: z.string().uuid(),
}

export const confirmUploadOutputShape = {
  valid: z.boolean(),
  error: z.string().nullable().optional(),
}

export type PresignedUploadInput = GetInferredFromRaw<typeof presignedUploadInputShape>
export type PresignedUploadOutput = GetInferredFromRaw<typeof presignedUploadOutputShape>
export type PresignedDownloadInput = GetInferredFromRaw<typeof presignedDownloadInputShape>
export type PresignedDownloadOutput = GetInferredFromRaw<typeof presignedDownloadOutputShape>
export type ConfirmUploadInput = GetInferredFromRaw<typeof confirmUploadInputShape>
export type ConfirmUploadOutput = GetInferredFromRaw<typeof confirmUploadOutputShape>
